import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useFunnelsApiFacade } from '../../../../apis/facades/useFunnelsApiFacade';
import {
  addSfFunnel,
  connectSfStages,
  selectFunnelMap,
  selectIsFunnelMapEmpty,
} from '../../../../reducers/united-canvas/unitedCanvasReducer';
import { setTransientFunnel } from '../../../../reducers/canvasPageStateReducer';
import { ACTIONS_EVENTS } from '../../../../services/events';
import useSendBiEvent from '../../../../hooks/useSendBiEvent';
import { FunnelType } from '../../../../types/enums/FunnelType';
import { setFunnelStatusFromFunnel } from '../../../../reducers/funnelStatusReducer';
import { useFunnelMapApiFacade } from '../../../../apis/facades/funnel-map/useFunnelMapApiFacade';
import { getFullMetadataElementId } from '../metadata-elements/metadataElementUtils';
import { StepToMetadataConnection } from '../../../funnel-map-canvas/funnelMapCanvasTypes';
import { telemetry } from '../../../../telemetry';
import {
  EnrichContextType,
  EnrichTargetType,
  MetadataConnectionType,
} from '@server/metadata-elements';

export const useSfAddFunnelToTheCanvas = (crmOrgId: string) => {
  const dispatch = useDispatch();
  const sendBiEvent = useSendBiEvent();
  const { get_funnel, post_funnel } = useFunnelsApiFacade();
  const { post_enrichMetadataElements } = useFunnelMapApiFacade();
  const isFunnelMapEmpty = useSelector(selectIsFunnelMapEmpty);
  const canvasFunnelMap = useSelector(selectFunnelMap);

  const getStepConnectionsForFunnel = useCallback(
    async (funnelId: string): Promise<StepToMetadataConnection[]> => {
      const hasMetadataElements = Object.keys(canvasFunnelMap.metadataElements ?? {}).length > 0;
      if (!hasMetadataElements || !canvasFunnelMap.id) {
        return [];
      }

      try {
        const enrichedElements = await post_enrichMetadataElements(crmOrgId, {
          targets: [
            { type: EnrichTargetType.AllMetadataFromFunnelMap, funnelMapId: canvasFunnelMap.id },
          ],
          context: [
            { type: EnrichContextType.FunnelMap, funnelMapId: canvasFunnelMap.id },
            { type: EnrichContextType.AllStepsFromFunnel, funnelId },
          ],
        });

        const stepConnections: StepToMetadataConnection[] = [];

        for (const enrichedElement of enrichedElements) {
          const targetMetadataElementId = getFullMetadataElementId(enrichedElement);

          for (const connection of enrichedElement.usedByConnections ?? []) {
            if (
              connection.type === MetadataConnectionType.FunnelStep &&
              connection.funnelId === funnelId
            ) {
              stepConnections.push({
                funnelId: connection.funnelId,
                stageId: connection.stageId,
                targetMetadataElementId,
              });
            }
          }
        }

        return stepConnections;
      } catch (err) {
        telemetry.captureError(err);
        return [];
      }
    },
    [canvasFunnelMap, crmOrgId, post_enrichMetadataElements],
  );

  const getConnectionsForNewStep = useCallback(
    async (
      funnelId: string,
      stageName: string,
      stageId: string,
    ): Promise<StepToMetadataConnection[]> => {
      const hasMetadataElements = Object.keys(canvasFunnelMap.metadataElements ?? {}).length > 0;
      if (!hasMetadataElements || !canvasFunnelMap.id) {
        return [];
      }

      try {
        const enrichedElements = await post_enrichMetadataElements(crmOrgId, {
          targets: [
            { type: EnrichTargetType.AllMetadataFromFunnelMap, funnelMapId: canvasFunnelMap.id },
          ],
          context: [{ type: EnrichContextType.FunnelStep, funnelId, stageName }],
        });

        const stepConnections: StepToMetadataConnection[] = [];

        for (const enrichedElement of enrichedElements) {
          const targetMetadataElementId = getFullMetadataElementId(enrichedElement);

          for (const connection of enrichedElement.usedByConnections ?? []) {
            if (
              connection.type === MetadataConnectionType.FunnelStep &&
              connection.funnelId === funnelId
            ) {
              stepConnections.push({
                funnelId: connection.funnelId,
                stageId: connection.stageId || stageId,
                targetMetadataElementId,
              });
            }
          }
        }

        return stepConnections;
      } catch (err) {
        telemetry.captureError(err);
        return [];
      }
    },
    [canvasFunnelMap, crmOrgId, post_enrichMetadataElements],
  );

  const dispatchStepConnections = useCallback(
    (stepConnections: StepToMetadataConnection[]) => {
      for (const connection of stepConnections) {
        dispatch(
          connectSfStages({
            stageA: {
              funnelId: connection.funnelId,
              stageId: connection.stageId,
            },
            stageB: {
              funnelId: connection.targetMetadataElementId,
              stageId: undefined,
              funnelType: FunnelType.METADATA_ELEMENT,
            },
          }),
        );
      }
    },
    [dispatch],
  );

  const createStepConnectionsForFunnel = useCallback(
    async (funnelId: string) => {
      const stepConnections = await getStepConnectionsForFunnel(funnelId);
      dispatchStepConnections(stepConnections);
    },
    [getStepConnectionsForFunnel, dispatchStepConnections],
  );

  const onCreateSfFunnel = useCallback(
    async ({
      funnelName,
      funnelDetails,
      position,
      sectionId,
    }: {
      funnelName: string;
      funnelDetails: FunnelDetails;
      position: { column: number; row: number };
      sectionId?: string;
    }) => {
      const funnel = await post_funnel(funnelName, crmOrgId, funnelDetails);
      sendBiEvent({
        name: ACTIONS_EVENTS.funnelsAddFunnel,
        props: { object: funnel.funnelDetails.leadingObject.objectName },
      });
      dispatch(
        addSfFunnel({
          funnel,
          position,
          sectionId,
        }),
      );

      // Get connections for all steps by stageName and dispatch them
      const stages = funnel.funnelDetails.stages;
      try {
        const allConnections = await Promise.all(
          stages.map((stage) =>
            getConnectionsForNewStep(funnel.id, stage.stageName, stage._stageId),
          ),
        );
        const flatConnections = allConnections.flat();
        dispatchStepConnections(flatConnections);
      } catch (err) {
        telemetry.captureError(err);
      }

      return funnel;
    },
    [
      crmOrgId,
      dispatchStepConnections,
      dispatch,
      getConnectionsForNewStep,
      post_funnel,
      sendBiEvent,
    ],
  );

  const addSfFunnelToTheCanvas = useCallback(
    async ({
      funnelId,
      isImportedRecordType,
    }: {
      funnelId: string;
      isImportedRecordType: boolean;
    }) => {
      const funnel = await get_funnel(funnelId);
      dispatch(setFunnelStatusFromFunnel(funnel));

      const stepConnections = await getStepConnectionsForFunnel(funnelId);

      const shouldAutoPlaceFirstFunnel = isFunnelMapEmpty;
      if (shouldAutoPlaceFirstFunnel) {
        dispatch(
          addSfFunnel({
            funnel,
            position: {
              column: 0,
              row: 0,
            },
          }),
        );
        dispatchStepConnections(stepConnections);
        return;
      }

      dispatch(
        setTransientFunnel({
          type: FunnelType.SALESFORCE,
          importType: 'import',
          data: { funnel, stepConnections },
          name: funnel.name,
          position: { column: 0, row: 0 },
          isPlacingFunnel: true,
          isImportedRecordType,
        }),
      );
    },
    [dispatch, dispatchStepConnections, get_funnel, getStepConnectionsForFunnel, isFunnelMapEmpty],
  );

  return {
    onCreateSfFunnel,
    addSfFunnelToTheCanvas,
    createStepConnectionsForFunnel,
    getStepConnectionsForFunnel,
    getConnectionsForNewStep,
    dispatchStepConnections,
  };
};
